import { forwardRef, useId, type ComponentType } from "react";
import { twMerge } from "tailwind-merge";
import { ExclamationCircleIcon } from "@components/icons";
import { Label } from "@components/Label/Label";

export interface ITextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  /**
   * Input label text.
   */
  label?: string;
  /**
   * Hint text. Will be hidden if error is set.
   */
  hint?: string;
  /**
   * Error text.
   */
  error?: string;
  /**
   * Icon element.
   */
  icon?: ComponentType<React.SVGProps<SVGSVGElement>>;
}

export const Textarea = forwardRef<HTMLTextAreaElement, ITextareaProps>(
  (props, ref) => {
    const {
      label,
      className,
      hint,
      error,
      icon: Icon,
      disabled,
      ...rest
    } = props;
    const inputId = useId();

    return (
      <div className="flex w-full flex-col gap-1.5">
        {label && <Label htmlFor={inputId}>{label}</Label>}
        <div
          className={twMerge(
            "relative w-full overflow-hidden rounded-xl border border-grey-hover transition-shadow focus-within:border-green-dark focus-within:ring-4 focus-within:ring-green-dark focus-within:ring-opacity-10",
            error &&
              "border-red-dark focus-within:border-red-dark focus-within:ring-red-dark",
            disabled && "bg-base-light-grey text-grey-dark",
          )}
        >
          <textarea
            id={inputId}
            className={twMerge(
              "relative z-10 block h-full w-full resize-none border-0 bg-transparent px-4 py-2.5 text-base-black outline-none placeholder:text-grey-dark disabled:text-grey-dark",
              (Icon ?? error) && "pr-10",
              className,
            )}
            disabled={disabled}
            ref={ref}
            {...rest}
          />
          {Icon && !error && (
            <Icon className="absolute right-4 top-2.5  z-0 m-auto h-5 w-5 text-grey-dark" />
          )}
          {error && (
            <ExclamationCircleIcon className="absolute right-4 top-2.5 z-0 m-auto h-5 w-5 text-red-dark" />
          )}
        </div>
        {hint && !error && (
          <span className="text-sm text-grey-dark">{hint}</span>
        )}
        {error && (
          <span className="break-all text-sm text-red-dark">{error}</span>
        )}
      </div>
    );
  },
);
